import { useState } from 'react';
import { Reorder, useDragControls, type DragControls } from 'motion/react';
import { cn } from '../lib/utils';
import { tapLight, tapMedium } from '../lib/haptics';

// One draggable row in a Reorder.Group, using the iOS edit-list pattern.
//
// Extracted from LineupBuilder so the Game Planner's Batting Order step and the
// live Lineup Builder share one implementation instead of drifting apart. Three
// details make this feel native, and all three are load-bearing:
//
//   1. `dragListener={false}` disables Framer's "drag from anywhere", so a
//      vertical flick on the card body SCROLLS the list instead of grabbing the
//      card. Only the grip handle starts a reorder, via dragControls.start().
//      `children` is a render prop so a handle nested deep in the row can reach
//      this row's controls.
//   2. `.reorder-lifted` (zIndex 999 !important, in src/index.css) beats the
//      inline zIndex:1 that Framer hardcodes onto a moving item. Without it the
//      held card tucks BEHIND its neighbors instead of floating above them.
//   3. No overflow-hidden on the item. Clipping makes the lift look like the
//      card is sliding behind its neighbor; the inner card keeps its own clip.
//
// Haptics complete the illusion on device: a tick when the card is picked up,
// a tick on each swap as it passes a neighbor, and a firmer thump on release.
// All three are native-only no-ops on web (see src/lib/haptics.ts).
//
// (Separate component so each row gets its own hooks; hooks cannot run in .map().)
export const ReorderRow = ({ value, children }: {
  value: string;
  children: (controls: DragControls) => React.ReactNode;
}) => {
  const dragControls = useDragControls();
  const [dragging, setDragging] = useState(false);
  return (
    <Reorder.Item
      value={value}
      dragListener={false}
      dragControls={dragControls}
      className={cn('relative rounded-2xl group flex flex-col reorder-row', dragging && 'reorder-lifted')}
      style={{ boxShadow: dragging ? '0 8px 24px rgba(20,26,33,0.18)' : 'none' }}
      onDragStart={() => { setDragging(true); tapLight(); }}
      onDragEnd={() => { setDragging(false); tapMedium(); }}
    >
      {children(dragControls)}
    </Reorder.Item>
  );
};
